import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const userVerificationRequests = new Map();

const fetchCurrentUser = (storedToken) => {
  if (!userVerificationRequests.has(storedToken)) {
    const request = axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${storedToken}` },
    }).finally(() => userVerificationRequests.delete(storedToken));
    userVerificationRequests.set(storedToken, request);
  }
  return userVerificationRequests.get(storedToken);
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('session_token'));
  const [loading, setLoading] = useState(true);

  // Attach token to all axios requests
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem('session_token');
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('session_token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // On mount, verify token by fetching /me
  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('session_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetchCurrentUser(storedToken);
        setUser(response.data);
        setToken(storedToken);
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.removeItem('session_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, []);

  const login = async () => {
    try {
      const response = await axios.get(`${API}/auth/login-url`);
      window.location.href = response.data.login_url;
    } catch (error) {
      console.error('Login initiation failed:', error);
      throw error;
    }
  };

  const handleCallback = async (code, state) => {
    const response = await axios.post(`${API}/auth/callback`, { code, state });
    const { session_token, user: userData } = response.data;
    localStorage.setItem('session_token', session_token);
    setToken(session_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setToken(null);
    setUser(null);
  };

  const pages = user?.pages || [];
  const hasPage = (pageKey) => pages.includes(pageKey);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, handleCallback, logout, pages, hasPage }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
